import Button from '@atoms/Button/Button';
import Carousel from '@atoms/Carousel/Carousel';
import Drawer from '@atoms/Drawer/Drawer';
import Icon from '@atoms/Icon/Icon';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import Text from '@atoms/Text/Text';
import SizeEnum from '@models/enums/SizeEnum';
import { IHotspotCard } from '@models/IHotspotCard';
import HotspotCard from '@molecules/HotspotCard/HotspotCard';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { SwiperSlide } from 'swiper/react';

export interface IInspirationOverlayLabels {
  readonly closeInspirationOverlayButtonLabel?: string;
  readonly errorMessage?: string;
  readonly loadingLabel?: string;
  readonly nextInspirationButtonLabel?: string;
  readonly previousInspirationButtonLabel?: string;
}

interface Props {
  readonly activeTooltipIndex: number;
  readonly contentLink?: string;
  readonly errors?: any;
  readonly hasMoreItemsToShow?: boolean;
  readonly initialIndex?: number;
  readonly inspirationOverlayLabels?: IInspirationOverlayLabels;
  readonly isMobile?: boolean;
  readonly isOpen: boolean;
  readonly items: IHotspotCard[];
  readonly loading?: boolean;

  readonly onRequestMore?: () => void | Promise<void>;
  readonly setIsOpen: (state: boolean) => void;
}

const InspirationOverlay = ({
  activeTooltipIndex = 0,
  contentLink = 'inspiration-carousel',
  errors,
  hasMoreItemsToShow,
  initialIndex = 0,
  inspirationOverlayLabels = {
    closeInspirationOverlayButtonLabel: 'Close',
    errorMessage: `Next image didn’t load. Try again`,
    loadingLabel: 'Loading',
    nextInspirationButtonLabel: 'Next image',
    previousInspirationButtonLabel: 'Previous image',
  },
  isMobile,
  isOpen,
  items,
  loading,
  onRequestMore,
  setIsOpen,
}: Props) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [isRtl, setIsRtl] = useState(false);
  const [showError, setShowError] = useState(false);

  const {
    closeInspirationOverlayButtonLabel,
    errorMessage,
    loadingLabel,
    nextInspirationButtonLabel,
    previousInspirationButtonLabel,
  } = inspirationOverlayLabels;

  useEffect(() => {
    const dir = document.documentElement.getAttribute('dir');
    setIsRtl(dir === 'rtl');
  }, []);

  const isStart = activeIndex === 0;
  const isEnd = activeIndex === items.length - 1;

  const handleNextClick = async () => {
    setShowError(false);

    if (isEnd) {
      if (onRequestMore) {
        try {
          await onRequestMore();

          if (
            errors &&
            typeof errors === 'object' &&
            Object.keys(errors).length > 0
          ) {
            setShowError(true);
          }
        } catch (error) {
          console.error('Error loading more items:', error);
          setShowError(true);
        } finally {
          swiper?.update();
          swiper?.updateSlides();
        }
      }
    } else {
      swiper?.slideNext();
    }
  };

  useEffect(() => {
    if (!swiper) return;

    const handleUpdate = () => {
      if (isEnd && hasMoreItemsToShow) {
        swiper.slideNext();
      }
    };

    swiper.on('update', handleUpdate);

    return () => {
      swiper.off('update', handleUpdate);
    };
  }, [swiper, isEnd, hasMoreItemsToShow]);

  useEffect(() => {
    if (
      errors &&
      typeof errors === 'object' &&
      Object.keys(errors).length > 0
    ) {
      setShowError(true);
    }
  }, [errors]);

  return (
    <Drawer
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      animateVerticallyOnMobile
      isRtl={isRtl}
      isInspirationOverlay
      closeButtonVariant="filled"
      wrapperStyles=""
      drawerClassnames={classNames(
        '!tw-z-90 tw-max-h-[100dvh] landscape:!tw-h-[100dvh] landscape:!tw-max-w-full landscape:!tw-w-[calc(100dvh*4/5)] !tw-w-screen !tw-mt-0',
        isMobile && '!tw-bottom-0 !tw-top-auto !tw-h-auto',
        items.length > 1
          ? 'portrait:!tw-h-[calc(100vw*5/4+52px)]'
          : 'portrait:!tw-h-[calc(100vw*5/4)]'
      )}
      closeLabel={closeInspirationOverlayButtonLabel}
    >
      <div className="tw-relative">
        <Carousel
          id={contentLink}
          onSwiper={(s) => {
            setSwiper(s);
            s.slideTo(initialIndex, 0);
          }}
          onSlideChange={(s) => setActiveIndex(s.activeIndex)}
          speed={800}
          allowTouchMove
        >
          <AnimatePresence mode="wait">
            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.7 }}
                exit={{ opacity: 0 }}
                className="tw-absolute tw-inset-0 tw-z-20 tw-flex tw-items-center tw-justify-center tw-bg-grey-20"
              >
                <LoadingSpinner
                  size={SizeEnum.Large}
                  loadingLabel={loadingLabel || 'Loading'}
                  background="!tw-bg-grey-20 !tw-border-grey-20"
                />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait">
            {showError && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="tw-absolute tw-bottom-[68px] tw-left-0 tw-right-0 tw-z-20 tw-mx-auto tw-flex tw-w-[calc(100%-32px)] tw-max-w-[580px] tw-items-center tw-justify-between tw-gap-2 tw-bg-grey-20 tw-p-xs"
              >
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Icon
                    name="notification"
                    className="tw-text-xl"
                    size="1rem"
                  />
                  <Text variant="body-small">{errorMessage}</Text>
                </div>
                <button
                  type="button"
                  aria-label={closeInspirationOverlayButtonLabel}
                  onClick={() => setShowError(false)}
                >
                  <Icon
                    name="cross"
                    className="tw-cursor-pointer tw-text-xl"
                    size="1rem"
                  />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          {items.map((item, index) => (
            <SwiperSlide key={`${item.link}-${index}`}>
              <HotspotCard
                {...item}
                useNewHotspotButton
                initialTooltipIndex={
                  index === initialIndex
                    ? activeTooltipIndex
                    : activeTooltipIndex === -1
                    ? -1
                    : 0
                }
                displayInOverlay
              />
            </SwiperSlide>
          ))}

          {items.length > 1 && (
            <div className="tw-bottom-0 tw-z-90 tw-flex tw-h-[52px] tw-w-full tw-divide-x tw-divide-grey-30 tw-bg-white landscape:tw-absolute">
              <Button
                id={`prevBtn-${contentLink}`}
                aria-label={previousInspirationButtonLabel}
                disabled={isStart || loading}
                label={previousInspirationButtonLabel}
                variant="whiteAlt"
                onClick={() => swiper?.slidePrev()}
                iconName="chevron-left"
                iconSize=".75rem"
                buttonClasses="tw-flex tw-h-full tw-basis-1/2 tw-rounded-none rtl:tw-flex-row-reverse"
                iconClasses="rtl:tw-rotate-180"
                contentClasses="!tw-gap-1 tw-flex-row-reverse"
              />
              <Button
                id={`nextBtn-${contentLink}`}
                aria-label={nextInspirationButtonLabel}
                label={nextInspirationButtonLabel}
                iconName="chevron-right"
                variant="whiteAlt"
                disabled={loading || (!hasMoreItemsToShow && isEnd)}
                onClick={handleNextClick}
                iconSize=".75rem"
                buttonClasses="tw-flex tw-h-full tw-basis-1/2 tw-rounded-none rtl:tw-flex-row-reverse"
                iconClasses="rtl:tw-rotate-180"
                contentClasses="!tw-gap-1"
              />
            </div>
          )}
        </Carousel>
      </div>
    </Drawer>
  );
};

export default InspirationOverlay;
